import type { SaveData } from '../../core/domain/types';
import { gameDatabase, type SaveRow } from '../../infrastructure/database/gameDatabase';
import { migrateSaveData } from '../migrations/saveMigrations';
import { saveDataSchema } from '../schemas/saveSchema';

export interface SaveRepository {
  loadMain(): Promise<SaveData | null>;
  loadBackup(): Promise<SaveData | null>;
  save(snapshot: SaveData): Promise<void>;
  preserveCorrupt(raw: unknown): Promise<void>;
}

export interface SaveStorage {
  get(id: string): Promise<SaveRow | undefined>;
  put(row: SaveRow): Promise<unknown>;
  delete(id: string): Promise<void>;
  transaction<T>(work: () => Promise<T>): Promise<T>;
}

function validate(raw: unknown): SaveData | null {
  try {
    const result = saveDataSchema.safeParse(migrateSaveData(raw));
    return result.success ? (result.data as SaveData) : null;
  } catch {
    return null;
  }
}

function recoveryId(nowUtc: number): string {
  const suffix = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);
  return 'recovery-' + nowUtc + '-' + suffix;
}

export function createSaveRepository(storage: SaveStorage): SaveRepository {
  const preserveCorrupt = async (raw: unknown): Promise<void> => {
    const now = Date.now();
    await storage.put({
      id: recoveryId(now),
      schemaVersion: 0,
      updatedAtUtc: now,
      payload: raw,
    });
  };

  const load = async (id: 'main' | 'backup'): Promise<SaveData | null> => {
    const row = await storage.get(id);
    if (!row) return null;
    const valid = validate(row.payload);
    if (valid) return valid;

    // Preserve the exact unreadable value, then remove the bad slot so a
    // later save cannot copy it over a healthy backup.
    await storage.transaction(async () => {
      await preserveCorrupt({ sourceSlot: id, originalRow: row });
      await storage.delete(id);
    });
    return null;
  };

  return {
    loadMain: () => load('main'),
    loadBackup: () => load('backup'),
    async save(snapshot) {
      const valid = saveDataSchema.parse(migrateSaveData(snapshot)) as SaveData;
      await storage.transaction(async () => {
        const main = await storage.get('main');
        if (main) await storage.put({ ...main, id: 'backup' });
        await storage.put({
          id: 'main',
          schemaVersion: valid.schemaVersion,
          updatedAtUtc: valid.lastSavedAtUtc,
          payload: valid,
        });
      });
    },
    preserveCorrupt,
  };
}

const indexedDbStorage: SaveStorage = {
  get: (id) => gameDatabase.saves.get(id),
  put: (row) => gameDatabase.saves.put(row),
  delete: (id) => gameDatabase.saves.delete(id),
  transaction: (work) => gameDatabase.transaction('rw', gameDatabase.saves, work),
};

export const indexedDbSaveRepository = createSaveRepository(indexedDbStorage);
